import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../stores/authStore';
import { usePermissions } from '../../hooks/usePermissions';
import PageHelp from '../../components/PageHelp';

interface CustomReportTemplate {
  id: string;
  name: string;
  description?: string;
  reportType: string;
  accountIds: string[];
  accountTypeFilter?: string;
  costCenterIds: string[];
  grouping?: string;
  sorting?: string;
  dateRangeType: string;
  includeSubAccounts: boolean;
  showZeroBalances: boolean;
  isActive: boolean;
  createdAt: string;
  creator?: {
    firstName: string;
    lastName: string;
  };
}

interface Account {
  id: string;
  code: string;
  name: string;
  type: string;
  parentId?: string;
}

interface CostCenter {
  id: string;
  name: string;
  code?: string;
}

const REPORT_TYPES = [
  { value: 'PROFIT_LOSS', label: 'Profit & Loss' },
  { value: 'BALANCE_SHEET', label: 'Balance Sheet' },
  { value: 'TRIAL_BALANCE', label: 'Trial Balance' },
  { value: 'GENERAL_LEDGER', label: 'General Ledger' },
  { value: 'ACCOUNT_STATEMENT', label: 'Account Statement' },
  { value: 'COST_CENTER_REPORT', label: 'Cost Center Report' },
];

const DATE_RANGE_TYPES = [
  { value: 'CUSTOM', label: 'Custom Date Range' },
  { value: 'LAST_MONTH', label: 'Last Month' },
  { value: 'LAST_QUARTER', label: 'Last Quarter' },
  { value: 'LAST_YEAR', label: 'Last Year' },
  { value: 'YTD', label: 'Year to Date' },
];

const GROUPING_OPTIONS = [
  { value: 'by_account', label: 'By Account' },
  { value: 'by_cost_center', label: 'By Cost Center' },
  { value: 'by_date', label: 'By Date' },
];

const SORTING_OPTIONS = [
  { value: 'ascending', label: 'Ascending' },
  { value: 'descending', label: 'Descending' },
  { value: 'by_amount', label: 'By Amount' },
];

const ACCOUNT_TYPES = [
  { value: 'ASSET', label: 'Assets' },
  { value: 'LIABILITY', label: 'Liabilities' },
  { value: 'EQUITY', label: 'Equity' },
  { value: 'INCOME', label: 'Income' },
  { value: 'EXPENSE', label: 'Expenses' },
];

const CustomReportTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<CustomReportTemplate[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<CustomReportTemplate | null>(null);
  const [generatedReport, setGeneratedReport] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    reportType: 'PROFIT_LOSS',
    accountIds: [] as string[],
    accountTypeFilter: '',
    costCenterIds: [] as string[],
    grouping: '',
    sorting: '',
    dateRangeType: 'CUSTOM',
    includeSubAccounts: true,
    showZeroBalances: false,
  });

  const [generateFormData, setGenerateFormData] = useState({
    periodStart: '',
    periodEnd: '',
  });

  useEffect(() => {
    fetchTemplates();
    fetchAccounts();
    fetchCostCenters();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get('/api/erp/custom-report-templates');
      if (response.data.success) {
        setTemplates(response.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load templates');
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/erp/accounts', { params: { isActive: 'true' } });
      if (response.data.success) {
        setAccounts(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    }
  };

  const fetchCostCenters = async () => {
    try {
      const response = await axios.get('/api/erp/cost-centers');
      if (response.data.success) {
        setCostCenters(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch cost centers:', err);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const response = await axios.post('/api/erp/custom-report-templates', formData);
      if (response.data.success) {
        setShowCreateModal(false);
        resetForm();
        fetchTemplates();
        toast.success('Template created successfully');
      } else {
        setError(response.data.error || 'Failed to create template');
        toast.error(response.data.error || 'Failed to create template');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to create template';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleEdit = (template: CustomReportTemplate) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      reportType: template.reportType,
      accountIds: template.accountIds || [],
      accountTypeFilter: template.accountTypeFilter || '',
      costCenterIds: template.costCenterIds || [],
      grouping: template.grouping || '',
      sorting: template.sorting || '',
      dateRangeType: template.dateRangeType,
      includeSubAccounts: template.includeSubAccounts,
      showZeroBalances: template.showZeroBalances,
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTemplate) return;
    setError(null);

    try {
      const response = await axios.put(`/api/erp/custom-report-templates/${selectedTemplate.id}`, formData);
      if (response.data.success) {
        setShowEditModal(false);
        setSelectedTemplate(null);
        resetForm();
        fetchTemplates();
        toast.success('Template updated successfully');
      } else {
        const errorMsg = response.data.error || 'Failed to update template';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to update template';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      const response = await axios.delete(`/api/erp/custom-report-templates/${id}`);
      if (response.data.success) {
        fetchTemplates();
        toast.success('Template deleted successfully');
      } else {
        const errorMsg = response.data.error || 'Failed to delete template';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to delete template';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleGenerate = (template: CustomReportTemplate) => {
    setSelectedTemplate(template);
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setGenerateFormData({
      periodStart: firstDay.toISOString().split('T')[0],
      periodEnd: lastDay.toISOString().split('T')[0],
    });
    setShowGenerateModal(true);
  };

  const handleGenerateReport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTemplate) return;
    setError(null);

    try {
      const response = await axios.post(`/api/erp/custom-report-templates/${selectedTemplate.id}/generate`, generateFormData);
      if (response.data.success) {
        setGeneratedReport(response.data.data);
        toast.success('Report generated successfully');
      } else {
        const errorMsg = response.data.error || 'Failed to generate report';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to generate report';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      reportType: 'PROFIT_LOSS',
      accountIds: [],
      accountTypeFilter: '',
      costCenterIds: [],
      grouping: '',
      sorting: '',
      dateRangeType: 'CUSTOM',
      includeSubAccounts: true,
      showZeroBalances: false,
    });
  };

  const toggleAccountSelection = (accountId: string) => {
    setFormData(prev => ({
      ...prev,
      accountIds: prev.accountIds.includes(accountId)
        ? prev.accountIds.filter(id => id !== accountId)
        : [...prev.accountIds, accountId],
    }));
  };

  const toggleCostCenterSelection = (costCenterId: string) => {
    setFormData(prev => ({
      ...prev,
      costCenterIds: prev.costCenterIds.includes(costCenterId)
        ? prev.costCenterIds.filter(id => id !== costCenterId)
        : [...prev.costCenterIds, costCenterId],
    }));
  };

  const renderReportData = (data: any) => {
    if (!data) return null;

    if (Array.isArray(data)) {
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {Object.keys(data[0] || {}).map(key => (
                  <th key={key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((row: any, idx: number) => (
                <tr key={idx}>
                  {Object.values(row).map((value: any, valIdx: number) => (
                    <td key={valIdx} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {typeof value === 'number' ? value.toLocaleString() : String(value || '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return <pre className="bg-gray-50 p-4 rounded overflow-auto">{JSON.stringify(data, null, 2)}</pre>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading templates...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <h1 className="text-2xl font-bold text-gray-900">Custom Report Templates</h1>
          <PageHelp articleKey="erp_custom_report" />
        </div>
        <div>
          <p className="mt-1 text-sm text-gray-500">
            Create and manage custom financial report templates
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          New Template
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {templates.length === 0 ? (
            <li className="px-6 py-8 text-center text-gray-500">
              No custom report templates found. Create your first template to get started.
            </li>
          ) : (
            templates.map(template => (
              <li key={template.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
                        {template.description && (
                          <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                        )}
                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-500">
                          <span className="inline-flex items-center">
                            <span className="font-medium">Type:</span> {REPORT_TYPES.find(t => t.value === template.reportType)?.label}
                          </span>
                          <span className="inline-flex items-center">
                            {DATE_RANGE_TYPES.find(t => t.value === template.dateRangeType)?.label}
                          </span>
                          {template.accountIds.length > 0 && (
                            <span>{template.accountIds.length} account(s)</span>
                          )}
                          {template.costCenterIds.length > 0 && (
                            <span>{template.costCenterIds.length} cost center(s)</span>
                          )}
                          {!template.isActive && (
                            <span className="text-orange-600">Inactive</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleGenerate(template)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Generate
                    </button>
                    <button
                      onClick={() => handleEdit(template)}
                      className="text-indigo-600 hover:text-indigo-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(template.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Create Custom Report Template</h3>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-gray-400 hover:text-gray-500">
                ×
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Report Type *</label>
                  <select
                    required
                    value={formData.reportType}
                    onChange={(e) => setFormData({ ...formData, reportType: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {REPORT_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Date Range Type</label>
                  <select
                    value={formData.dateRangeType}
                    onChange={(e) => setFormData({ ...formData, dateRangeType: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {DATE_RANGE_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Account Type Filter</label>
                <select
                  value={formData.accountTypeFilter}
                  onChange={(e) => setFormData({ ...formData, accountTypeFilter: e.target.value })}
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">All Account Types</option>
                  {ACCOUNT_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Accounts</label>
                <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
                  {accounts.map(account => (
                    <label key={account.id} className="flex items-center py-1">
                      <input
                        type="checkbox"
                        checked={formData.accountIds.includes(account.id)}
                        onChange={() => toggleAccountSelection(account.id)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {account.code} - {account.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Cost Centers</label>
                <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
                  {costCenters.map(cc => (
                    <label key={cc.id} className="flex items-center py-1">
                      <input
                        type="checkbox"
                        checked={formData.costCenterIds.includes(cc.id)}
                        onChange={() => toggleCostCenterSelection(cc.id)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {cc.code ? `${cc.code} - ` : ''}{cc.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Grouping</label>
                  <select
                    value={formData.grouping}
                    onChange={(e) => setFormData({ ...formData, grouping: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">No Grouping</option>
                    {GROUPING_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Sorting</label>
                  <select
                    value={formData.sorting}
                    onChange={(e) => setFormData({ ...formData, sorting: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Default Sorting</option>
                    {SORTING_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.includeSubAccounts}
                    onChange={(e) => setFormData({ ...formData, includeSubAccounts: e.target.checked })}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Include Sub-Accounts</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.showZeroBalances}
                    onChange={(e) => setFormData({ ...formData, showZeroBalances: e.target.checked })}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Show Zero Balances</span>
                </label>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Create Template
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedTemplate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Custom Report Template</h3>
              <button onClick={() => { setShowEditModal(false); setSelectedTemplate(null); resetForm(); }} className="text-gray-400 hover:text-gray-500">
                ×
              </button>
            </div>
            <form onSubmit={handleUpdate} className="space-y-4">
              {/* Same form fields as create modal */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Report Type *</label>
                  <select
                    required
                    value={formData.reportType}
                    onChange={(e) => setFormData({ ...formData, reportType: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {REPORT_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Date Range Type</label>
                  <select
                    value={formData.dateRangeType}
                    onChange={(e) => setFormData({ ...formData, dateRangeType: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {DATE_RANGE_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Account Type Filter</label>
                <select
                  value={formData.accountTypeFilter}
                  onChange={(e) => setFormData({ ...formData, accountTypeFilter: e.target.value })}
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">All Account Types</option>
                  {ACCOUNT_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Accounts</label>
                <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
                  {accounts.map(account => (
                    <label key={account.id} className="flex items-center py-1">
                      <input
                        type="checkbox"
                        checked={formData.accountIds.includes(account.id)}
                        onChange={() => toggleAccountSelection(account.id)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {account.code} - {account.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Cost Centers</label>
                <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
                  {costCenters.map(cc => (
                    <label key={cc.id} className="flex items-center py-1">
                      <input
                        type="checkbox"
                        checked={formData.costCenterIds.includes(cc.id)}
                        onChange={() => toggleCostCenterSelection(cc.id)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {cc.code ? `${cc.code} - ` : ''}{cc.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Grouping</label>
                  <select
                    value={formData.grouping}
                    onChange={(e) => setFormData({ ...formData, grouping: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">No Grouping</option>
                    {GROUPING_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Sorting</label>
                  <select
                    value={formData.sorting}
                    onChange={(e) => setFormData({ ...formData, sorting: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Default Sorting</option>
                    {SORTING_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.includeSubAccounts}
                    onChange={(e) => setFormData({ ...formData, includeSubAccounts: e.target.checked })}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Include Sub-Accounts</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.showZeroBalances}
                    onChange={(e) => setFormData({ ...formData, showZeroBalances: e.target.checked })}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Show Zero Balances</span>
                </label>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setSelectedTemplate(null); resetForm(); }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Update Template
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Generate Report Modal */}
      {showGenerateModal && selectedTemplate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Generate Report: {selectedTemplate.name}</h3>
              <button onClick={() => { setShowGenerateModal(false); setSelectedTemplate(null); setGeneratedReport(null); }} className="text-gray-400 hover:text-gray-500">
                ×
              </button>
            </div>
            {!generatedReport ? (
              <form onSubmit={handleGenerateReport} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Period Start *</label>
                    <input
                      type="date"
                      required
                      value={generateFormData.periodStart}
                      onChange={(e) => setGenerateFormData({ ...generateFormData, periodStart: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Period End *</label>
                    <input
                      type="date"
                      required
                      value={generateFormData.periodEnd}
                      onChange={(e) => setGenerateFormData({ ...generateFormData, periodEnd: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowGenerateModal(false); setSelectedTemplate(null); }}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    Generate Report
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded">
                  <h4 className="font-medium text-gray-900 mb-2">Generated Report Data</h4>
                  {renderReportData(generatedReport.data)}
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => { setGeneratedReport(null); }}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Generate Another
                  </button>
                  <button
                    onClick={() => { setShowGenerateModal(false); setSelectedTemplate(null); setGeneratedReport(null); }}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomReportTemplates;
